/* eslint-disable no-console */
// @flow
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Head } from '@finect/tabular-components/Head';
import styled from 'styled-components';
import { convertToRaw } from 'draft-js';
import { Strong } from '@finect/tabular-core/Strong';
import { Small } from '@finect/tabular-core/Small';
import { Button } from '@finect/tabular-core/Button';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { MainContent } from '@finect/tabular-core/MainContent';
import { Side } from '@finect/tabular-core/Side';
import { Tooltip } from '@finect/tabular-components/Tooltip';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { getFont } from '@finect/tabular-utils/getFont';
import { EntryEditor } from '@finect/tabular-draft-editor';
import { slugify } from '@finect/front-utils/slugify';
import { finectAPI } from '@finect/front-utils/finectAPI';
import type { Editor } from 'draft-js';
import { DatalayerScene } from '@finect/front-gtm';
import type { WithParamsProps } from '@finect/front-core/withParams';
import { withParams } from '@finect/front-core/withParams';
import { getProductPath } from '@finect/front-resources/Products';
import { getLoginBaseURL } from '@finect/front-utils/getLoginBaseURL';
import type { UserType, TagType } from '../../types';
import {
  MentionedProducts,
  TagAutocomplete,
  EntryContent,
  QuestionDetail,
  QuestionDetailInner,
  Label,
  Sublabel,
  QuestionTitle,
  TitleInput,
  SideTitle
} from '../../components';

import { getFromLocal, updateLocal } from '../../utils';
import { sendQuestion } from '../../store/questions/actions';

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ sendQuestion }, dispatch)
});

const mapStateToProps = (state: Object) => ({
  user: state.user.user
});

const TAG_LIMIT = 5;

/* eslint-disable */
const Publication = styled.div`
  flex: 1;
  padding: 0 30px 24px;
  text-align: right;

  ${maxMediaQueries.phablet`
    padding: 0 15px 24px;
  `};
`;
/* eslint-enable */

const AbandonPublication = styled.div`
  padding: 0 39px;
  margin-top: 12px;
  text-align: right;

  ${maxMediaQueries.phablet`
    text-align: center;
  `};
`;

const Abandon = styled.button`
  width: 180px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.bodyFontFamily};
  ${getFont(0)};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.fontL};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.font};
  }
`;

const Tips = styled.div`
  > div:not(:first-child) {
    padding-top: 9px;
  }

  small {
    display: block;
    padding-bottom: 12px;
  }
`;

type Props = {
  user: UserType,
  location: Object,
  history: Object,
  match: Object,
  actions: Object,
  staticContext?: Object
} & WithParamsProps;

type State = {
  // TODO Define product Type
  selectedProducts: Array<Object>,
  selectedTags: Array<TagType>,
  initialProduct: Object | null,
  hasValidTitle: boolean,
  group: string | null
};

class NewQuestion extends React.Component<Props, State> {
  titleEditor: Editor;
  questionEditor: Editor;

  state = {
    selectedProducts: getFromLocal('questionProducts') || [],
    selectedTags: getFromLocal('questionTags') || [],
    initialProduct: null,
    hasValidTitle: false,
    group: null
  };

  componentDidMount() {
    // Checking query params
    const { params } = this.props;

    if (params.group) {
      this.setState({ group: params.group });
    }

    if (params && params.tag) {
      this.getTag(params.tag).then((tag) => {
        if (!tag) return;
        this.addTag(tag);
      });
    }

    if (params && params.product) {
      this.getProduct(params.product).then((product) => {
        if (!product) return;

        const newProduct = {
          id: product.entity.id,
          name: product.entity.name,
          link: getProductPath(product)
        };

        this.setState({
          initialProduct: newProduct
        });

        this.addProduct(product);
      });
    }

    setTimeout(() => {
      this.focusTitleEditor();
    }, 0);
  }

  focusEditor = () => {
    this.questionEditor.focus();
  };

  focusTitleEditor = () => {
    this.titleEditor.focus();
  };

  handleFocus = () => {
    // $FlowFixMe
    if (document.activeElement.hasAttribute('contenteditable')) return;
    const title = this.titleEditor.props.editorState.getCurrentContent();
    const body = this.questionEditor.props.editorState.getCurrentContent();
    if (!title.hasText() && !body.hasText()) {
      this.focusTitleEditor();
    } else {
      this.focusEditor();
    }
  };

  handleBackspace = () => {
    const content = this.questionEditor.props.editorState.getCurrentContent();
    if (!content.hasText()) {
      // It would also be nice to position the caret at the end
      this.focusTitleEditor();
    }
  };

  handleReturn = () => {
    this.focusEditor();
  };

  handleChange = (content: string) => {
    const validContent = Boolean(content && content.length <= 120);
    const { hasValidTitle } = this.state;
    if (validContent !== hasValidTitle) {
      this.setState({ hasValidTitle: validContent });
    }
  };

  getProduct = (id: string) =>
    finectAPI({
      version: 'v4',
      path: `products/${id}`,
      method: 'GET',
      onSuccess: (product: Object) => product.data,
      onError: error => console.log(error)
    });

  addProduct = (newProduct: Object) => {
    const newProducts = [...this.state.selectedProducts];
    const pos = newProducts.findIndex((product) => {
      const id = (newProduct.entity && newProduct.entity.id) || newProduct.id;
      return product.entity.id === id;
    });

    if (pos === -1) {
      newProducts.push(newProduct);
      this.setState({ selectedProducts: newProducts });
      updateLocal('questionProducts', newProducts);
    }
  };

  removeProduct = (products: Array<Object>) => {
    const { selectedProducts } = this.state;

    // Nothing to delete
    if (!selectedProducts.length) return;
    if (products.length === selectedProducts.length) return;

    // All previous products have been removed
    if (!products.length) {
      this.setState({ selectedProducts: [] });
      return updateLocal('questionProducts', []);
    }

    // Remove deleted products
    const newProducts = selectedProducts.filter(product => products.includes(product.entity.id));
    updateLocal('questionProducts', newProducts);
    this.setState({ selectedProducts: newProducts });
  };

  getTag = (id: number) =>
    finectAPI({
      version: 'v4',
      path: `contents/tags/${id}`,
      method: 'GET',
      onSuccess: (tag: Object) => tag.data,
      onError: error => console.log(error)
    });

  addTag = (tag: TagType) => {
    const newTags = [...this.state.selectedTags];
    const exists = newTags.some(currentTag => currentTag.id === tag.id);
    if (!exists && newTags.length < TAG_LIMIT) {
      newTags.push(tag);
      this.setState({ selectedTags: newTags });
    }
    updateLocal('questionTags', newTags);
  };

  removeTag = (tagId: number) => {
    const newTags = this.state.selectedTags.filter(tag => tag.id !== tagId);
    this.setState({ selectedTags: newTags });
    updateLocal('questionTags', newTags);
  };

  removeLatestTag = () => {
    const { selectedTags } = this.state;
    if (selectedTags.length === 0) return;
    const newTags = selectedTags.slice(0, -1);
    this.setState({ selectedTags: newTags });
    updateLocal('questionTags', newTags);
  };

  hashQuestion = (question: Object) => {
    const baseURL = `https://${process.env.FINECT_BASEURL || 'www.finect.com'}`;

    const blob = {
      copy: 'Para publicar tu pregunta accede a tu cuenta de Finect',
      action: 'publish',
      target: question,
      next: `${baseURL}/preguntas/mis-preguntas?moderation=pending`
    };
    return Buffer.from(JSON.stringify(blob)).toString('base64');
  };

  publishQuestion = () => {
    const content = this.questionEditor.props.editorState.getCurrentContent();

    const title = this.titleEditor.props.editorState.getCurrentContent();
    if (!title.hasText()) return;

    const rawContent = convertToRaw(content);
    const plainText = content.getPlainText();

    const tags = this.state.selectedTags ? this.state.selectedTags.map(tag => tag.id).join(', ') : '';

    const products = this.state.selectedProducts;
    const group = this.state.group;

    const newQuestion = {
      title: title.getPlainText(),
      rawContent,
      plainText,
      tags,
      products,
      group
    };

    console.log(newQuestion);

    setTimeout(() => {
      localStorage.removeItem('questionTitle');
      localStorage.removeItem('questionContent');
      localStorage.removeItem('questionProducts');
      localStorage.removeItem('questionTags');
      localStorage.removeItem('group');
    }, 250);

    if (this.props.user.isLoggedIn) {
      const redirect = (question) => {
        const path = `/preguntas/${question.id}/${slugify(newQuestion.title)}?moderation=pending`;
        this.props.history.push(path);
      };
      this.props.actions.sendQuestion(newQuestion, redirect);
    } else {
      const path = `${getLoginBaseURL()}/login?action=${this.hashQuestion(newQuestion)}`;
      window.location.replace(path);
    }
  };

  cancelPublication = () => {
    // Deletes latest localstorage content even if debounced
    setTimeout(() => {
      localStorage.removeItem('questionTitle');
      localStorage.removeItem('questionContent');
    }, 250);
    localStorage.removeItem('questionProducts');
    localStorage.removeItem('questionTags');

    this.props.history.push({
      pathname: '/preguntas',
      state: { forceFetch: true }
    });
  };

  render() {
    const { location, params } = this.props;
    const { selectedProducts, selectedTags, hasValidTitle, initialProduct } = this.state;
    const disabled = !hasValidTitle || selectedTags.length === 0;
    const props = {};

    if (params && params.question) {
      props.content = {
        type: 'plain',
        body: params.question
      };
    }

    /* eslint-disable */
    return (
      <Container>
        <Head
          key={location.pathname}
          title="¿Cuál es tu pregunta? - Preguntas"
          description="Envíanos tu pregunta"
          canonical="/preguntas/nueva-pregunta"
          robots="noindex, nofollow"
        />

        <DatalayerScene pageType="questionNew" />

        <ContainerInner>
          <MainContent>
            <QuestionDetail prePublish>
              <QuestionDetailInner>
                <EntryContent>
                  <Label>¿Tienes una duda sobre finanzas, inversiones o la gestión de tu dinero?</Label>
                  <Sublabel>Sé específico e imagina que estás preguntando directamente a alguien</Sublabel>
                  <QuestionTitle>
                    <TitleInput className={'insert'} as={'div'}>
                      <EntryEditor
                        minimal
                        inputRef={title => (this.titleEditor = title)}
                        minHeight={{ size: 160, apply: 'miniPhone' }}
                        name="questionTitle"
                        placeholder="Escribe aquí tu pregunta"
                        postEntry={this.publishQuestion}
                        handleFocus={this.focusTitleEditor}
                        focusEditor={this.focusTitleEditor}
                        handleReturn={this.handleReturn}
                        handleChange={this.handleChange}
                        decorators={['limit', 'questionSuggestion']}
                        limit={{ chars: 120, extraPadding: 9 }}
                        {...props}
                      />
                    </TitleInput>
                  </QuestionTitle>
                  <br />
                  <Sublabel>Si necesitas incluir más detalle, escribelo aquí</Sublabel>
                  <div
                    style={{
                      boxSizing: 'border-box',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      padding: '5px',
                      backgroundColor: '#fdfdfd'
                    }}
                  >
                    <EntryEditor
                      inputRef={el => (this.questionEditor = el)}
                      minHeight={{ size: 120, apply: 'all' }}
                      name="questionContent"
                      placeholder="Puedes mencionar productos con escribiendo un $ y después el nombre del producto"
                      focusEditor={this.focusEditor}
                      handleFocus={this.handleFocus}
                      handleBackspace={this.handleBackspace}
                      handleAddProduct={this.addProduct}
                      handleRemoveProduct={this.removeProduct}
                      decorators={['mentions', 'products', 'link', 'inlink', 'images']}
                      entity={
                        initialProduct
                          ? {
                              type: 'PRODUCT_MENTION',
                              mutability: 'IMMUTABLE',
                              data: initialProduct
                            }
                          : null
                      }
                    />
                  </div>
                  <br />
                  <Label>Temas</Label>
                  <Sublabel>Añade al menos un tema, y hasta un máximo de 5, relacionados con tu pregunta</Sublabel>
                  <TagAutocomplete
                    selectedTags={selectedTags}
                    addTag={this.addTag}
                    removeTag={this.removeTag}
                    removeLatestTag={this.removeLatestTag}
                  />
                  {selectedProducts.length ? <MentionedProducts products={selectedProducts} /> : null}
                </EntryContent>
                <Publication>
                  <Tooltip
                    disabled={!disabled}
                    display="inline-block"
                    width="198px"
                    textAlign="center"
                    tip={
                      !hasValidTitle && selectedTags.length === 0
                        ? 'Escribe tu pregunta y añade un tema para poder publicar.'
                        : !hasValidTitle
                        ? 'Parece que hay un problema con tu pregunta, révisala para poder publicar.'
                        : 'Añade un tema para poder publicar.'
                    }
                  >
                    <Button secondary onClick={this.publishQuestion} disabled={disabled}>
                      Publica tu pregunta
                    </Button>
                  </Tooltip>
                </Publication>
              </QuestionDetailInner>
            </QuestionDetail>
            <AbandonPublication>
              <Abandon onClick={this.cancelPublication}>Abandonar publicación</Abandon>
            </AbandonPublication>
          </MainContent>
          <Side>
            <Tips>
              <SideTitle>Pregunta</SideTitle>
              <Small compact light>
                Para obtener una buena respuesta, escribe tu pregunta de forma breve y concisa. Puedes probar a comenzar
                con <em>&quot;¿Cómo...&quot;</em>,<em>&quot;¿Cuál...&quot;</em>, <em>&quot;¿Qué...&quot;</em> o{' '}
                <em>&quot;¿Por qué...&quot;</em>.
              </Small>
              <SideTitle>Menciones e imágenes</SideTitle>
              <Small compact light>
                Dentro del detalle de la pregunta, puedes mencionar productos usando el símbolo del <Strong>$</Strong>.
              </Small>
              <Small compact light>
                También puedes <Strong>insertar imágenes</Strong> arrastrando un fichero de tu ordenador al editor.
              </Small>
              <SideTitle>Temas</SideTitle>
              <Small compact light>
                Clasifica tu pregunta eligiendo uno o varios temas. Por ejemplo, si tu pregunta trata sobre la compra de
                una casa, puedes añadir los temas: <em>hipotecas</em> y <em>vivienda</em>.
              </Small>
            </Tips>
          </Side>
        </ContainerInner>
      </Container>
    );
  }
  /* eslint-enable */
}

export default connect(mapStateToProps, mapDispatchToProps)(withParams(NewQuestion));
